import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { ApolloError, useQuery, useMutation, Types } from '@grandvisionhq/graphql';
import { useSelector } from '@grandvisionhq/state';
import { getAuth } from '../../selectors.js';
import { isLoggedIn } from '../../state.js';
import { toOptIns } from '../mappers/opt-ins-mapper.js';
import UPDATE_OPT_INS from '../operations/mutations/update-opt-ins.js';
import GET_OPT_INS from '../operations/queries/get-opt-ins.js';
import { useAvailableOptIns } from './use-available-opt-ins.js';

const useOptIns = ({ onError , onCompleted  } = {})=>{
    const auth = useSelector(getAuth);
    const [optInsFromServer, setOptInsFromServer] = React.useState([]);
    let authError = undefined;
    const { availableOptIns  } = useAvailableOptIns();
    if (!isLoggedIn(auth)) {
        authError = new ApolloError({
            errorMessage: `Can't fetch opt-ins when user isn't logged in`
        });
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'AUTH',
            error: authError
        });
    }
    const handleQueryError = React.useCallback((error)=>{
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'QUERY',
            error
        });
    }, [
        onError
    ]);
    const handleMutationError = React.useCallback((error)=>{
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'MUTATION',
            error
        });
    }, [
        onError
    ]);
    const queryOptions = React.useMemo(()=>{
        return {
            skip: Boolean(authError),
            onError: handleQueryError,
            onCompleted: (data)=>{
                var ref;
                setOptInsFromServer(toOptIns((ref = data.me) === null || ref === void 0 ? void 0 : ref.optIns));
                onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('QUERY');
            }
        };
    }, [
        authError,
        handleQueryError,
        onCompleted
    ]);
    const query = useQuery(GET_OPT_INS, queryOptions);
    const mutationOptions = React.useMemo(()=>{
        return {
            skip: Boolean(authError),
            onError: handleMutationError,
            onCompleted: (data)=>{
                var ref;
                setOptInsFromServer(toOptIns((ref = data.updateB2COptIns) === null || ref === void 0 ? void 0 : ref.optIns));
                onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('MUTATION');
            }
        };
    }, [
        authError,
        handleMutationError,
        onCompleted
    ]);
    const [mutationCallback, mutation] = useMutation(UPDATE_OPT_INS, mutationOptions);
    const updateOptIns = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(changedOptIns) {
            /**
       * If there were no changes in opt-in values, act as if the mutation
       * completed successfully, without doing the actual mutation.
       */ if (!changedOptIns.length) {
                onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('MUTATION');
                return;
            }
            // Append with `uiType` for conversion to GQL interface
            const updatedOptIns = changedOptIns.map((optIn)=>swcHelpers.objectSpread({}, optIn, {
                    uiType: Types.UIType.CHECKBOX
                })
            );
            yield mutationCallback({
                variables: {
                    customerId: auth.userId,
                    optinUpdates: updatedOptIns
                }
            });
        });
        return function(changedOptIns) {
            return _ref.apply(this, arguments);
        };
    }(), [
        auth.userId,
        mutationCallback,
        onCompleted
    ]);
    return React.useMemo(()=>({
            optInsFromServer,
            availableOptIns,
            updateOptIns,
            isFetching: query.loading,
            fetchError: authError || query.error,
            isUpdating: mutation.loading,
            updateError: mutation.error
        })
    , [
        authError,
        availableOptIns,
        mutation.error,
        mutation.loading,
        optInsFromServer,
        query.error,
        query.loading,
        updateOptIns, 
    ]);
};

export { useOptIns };
//# sourceMappingURL=use-opt-ins.js.map
